// HQ drafts: a broadcast, recurring, drip or ad campaign. Prepare counts who would receive it. Nothing is sent from here.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { EmptyState, Loader, Modal, Spinner, WelcomeBar } from '../components/ui';

const TYPES = [
  { id: 'broadcast', label: 'Broadcast' },
  { id: 'recurring', label: 'Recurring' },
  { id: 'drip', label: 'Drip' },
  { id: 'ad', label: 'Ad' },
];

const EMPTY = { name: '', type: 'broadcast', template_name: '', holdout_pct: 0 };

function typeLabel(type) {
  return TYPES.find((t) => t.id === type)?.label || type || '—';
}

function CampaignForm({ onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try {
      await api.createCampaign({
        name: form.name.trim(),
        type: form.type,
        template_name: form.template_name.trim(),
        holdout_pct: Number(form.holdout_pct) || 0,
      });
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="New campaign" onClose={onClose}>
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">Name</span>
          <input className="de-input de-input--bordered" value={form.name} onChange={set('name')} placeholder="Diwali hair spa" />
        </label>
        <label className="de-field">
          <span className="de-label">Type</span>
          <select className="de-select" value={form.type} onChange={set('type')}>
            {TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className="de-field">
          <span className="de-label">Holdout %</span>
          <input className="de-input de-input--bordered" type="number" min={0} max={100} value={form.holdout_pct} onChange={set('holdout_pct')} />
        </label>
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">Template name</span>
          <input className="de-input de-input--bordered" value={form.template_name} onChange={set('template_name')} placeholder="Approved marketing template" />
        </label>
      </div>
      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={busy || !form.name.trim()} onClick={save}>
          {busy ? <Spinner light /> : null}
          Save
        </button>
      </div>
    </Modal>
  );
}

export default function Campaigns() {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [preparing, setPreparing] = useState(null);

  const load = () =>
    api
      .listCampaigns()
      .then(setRows)
      .catch((e) => {
        toast.error(e.message);
        setRows((current) => current ?? []);
      });

  useEffect(() => {
    load();
  }, []);

  const prepare = async (row) => {
    setPreparing(row.id);
    try {
      const res = await api.prepareCampaign(row.id);
      const count = res?.recipients ?? res?.recipient_count ?? res?.estimated_recipients ?? res?.count;
      toast.success(count == null ? 'Prepared' : `${count} recipients`);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPreparing(null);
    }
  };

  return (
    <div className="de-fade">
      <WelcomeBar title="Campaigns" sub="Draft broadcasts, recurring and drip sends, and ad campaigns. Prepare counts recipients. It does not send." />
      <div className="de-panel">
        <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div className="de-panel-title" style={{ margin: 0 }}>
            Campaigns
          </div>
          <button type="button" className="de-btn de-btn--primary" onClick={() => setCreating(true)}>
            New campaign
          </button>
        </div>
        {!rows ? (
          <Loader />
        ) : rows.length ? (
          <div className="de-table-shell">
            <div className="de-table-wrap">
              <table className="de-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Estimated recipients</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="de-td-strong">{row.name || '—'}</td>
                      <td>{typeLabel(row.type)}</td>
                      <td>
                        <span className="de-pill de-pill--sm de-pill--muted">{row.status || '—'}</span>
                      </td>
                      <td>{row.estimated_recipients ?? '—'}</td>
                      <td className="de-td-right">
                        <button
                          type="button"
                          className="de-btn de-btn--outline de-btn--sm"
                          disabled={preparing === row.id}
                          onClick={() => prepare(row)}
                        >
                          {preparing === row.id ? <Spinner /> : null}
                          Prepare
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState icon="📣" title="No campaigns yet" body="Create a draft. Preparing counts who would receive it." />
        )}
      </div>
      {creating ? (
        <CampaignForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
